export interface EquipamentoPedido {
  id: string;
  descricao: string;
  codigo_patrimonio: string | null;
  categoria: string | null;
  modelo: string | null;
}

export interface KitPedido {
  id: string;
  nome: string;
  codigo: string | null;
  status?: string;
  itens: Array<{ descricao: string; modelo?: string | null; codigo_patrimonio?: string | null }>;
  acessorios: Array<{ nome: string; qtd: number }>;
  rotulo?: string;
}

const PREFIXO_KIT = '__kit_campo__:';
export const valorKitPedido = (id: string) => `${PREFIXO_KIT}${id}`;
export const nomeKitPedido = (kit: KitPedido) => `Kit: ${kit.nome}${kit.codigo ? ` [${kit.codigo}]` : ''}`;

const nomeEquipamento = (item: KitPedido['itens'][number]) =>
  `${item.descricao}${item.modelo ? ` ${item.modelo}` : ''}${item.codigo_patrimonio ? ` [${item.codigo_patrimonio}]` : ''}`;

export function composicaoKitPedido(kit: KitPedido): string {
  const partes: string[] = [];
  if (kit.itens?.length) partes.push(`Itens: ${kit.itens.map(nomeEquipamento).join(', ')}`);
  if (kit.acessorios?.length) partes.push(`Acessórios: ${kit.acessorios.map(a => `${a.qtd}× ${a.nome}`).join(', ')}`);
  return partes.join(' · ') || 'Sem itens cadastrados';
}

// O rótulo completo é guardado para as telas e avisos antigos. No formulário,
// o kit continua sendo uma única seleção, ligada pelo id, sem duplicar as peças.
export function recursosSalvosPedido(dados: any): string[] {
  const kits: KitPedido[] = Array.isArray(dados?.kits) ? dados.kits : [];
  const rotulos = new Set(kits.map(k => k.rotulo).filter(Boolean));
  const equipamentos = Array.isArray(dados?.equipamentos) ? dados.equipamentos : [];
  return Array.from(new Set([
    ...equipamentos.filter((e: string) => !rotulos.has(e)),
    ...kits.map(k => valorKitPedido(k.id)),
  ]));
}

export function recursosParaSalvarPedido(valores: string[] = []) {
  const unicos = Array.from(new Set(valores));
  return {
    equipamentos: unicos.filter(v => !v.startsWith(PREFIXO_KIT)),
    kits_ids: unicos.filter(v => v.startsWith(PREFIXO_KIT)).map(v => v.slice(PREFIXO_KIT.length)),
  };
}

export function kitsDoPedido(catalogo: KitPedido[], salvos: KitPedido[] = []): KitPedido[] {
  // O snapshot salvo prevalece ao editar: mudar o cadastro do kit não pode
  // reescrever silenciosamente a composição de um pedido já aberto.
  const porId = new Map(catalogo.map(k => [k.id, k]));
  salvos.forEach(k => porId.set(k.id, { ...porId.get(k.id), ...k }));
  return Array.from(porId.values());
}

export function opcoesRecursosPedido(equipamentos: EquipamentoPedido[], kits: KitPedido[], dados?: any) {
  const salvos: KitPedido[] = Array.isArray(dados?.kits) ? dados.kits : [];
  const idsSalvos = new Set(salvos.map(k => k.id));
  const grupos = [{
    label: 'Kits de campo',
    options: kitsDoPedido(kits, salvos).map(k => ({
      value: valorKitPedido(k.id),
      label: `${nomeKitPedido(k)}${k.status === 'em_campo' ? ' · Em campo' : k.status === 'manutencao' ? ' · Manutenção' : ''}`,
      disabled: !idsSalvos.has(k.id) && ['manutencao', 'inativo'].includes(k.status || ''),
    })),
  }, ...Array.from(new Set(equipamentos.map(e => e.categoria || 'Outros'))).map(cat => ({
    label: cat,
    options: equipamentos.filter(e => (e.categoria || 'Outros') === cat)
      .map(e => ({ value: nomeEquipamento(e), label: nomeEquipamento(e), disabled: false })),
  }))];
  const existentes = new Set(grupos.flatMap(g => g.options.map(o => o.value)));
  const foraDoCatalogo = recursosSalvosPedido(dados).filter(v => !existentes.has(v));
  if (foraDoCatalogo.length) grupos.push({
    label: 'Já informados no pedido',
    options: foraDoCatalogo.map(v => ({ value: v, label: v, disabled: false })),
  });
  return grupos.filter(g => g.options.length > 0);
}
